import { FC, useState, useRef, useEffect, useMemo } from "react";
import { useNavigation } from "../context/NavigationContext";
import WalletConnect from "./wallet/WalletConnect";
import { CloudIcon, PlusIcon, BoltIcon, BellIcon } from "@heroicons/react/24/solid";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { useGateways } from "../context/GatewayContext";
import PlusCpuIcon from "./icons/PlusCpuIcon";
import { useWalletContext } from "../context/WalletContext";
import { WalletTypeDetector } from "../wallets/WalletTypeDetector";
import CrownIcon from "./icons/CrownIcon";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const Header: FC = () => {
  const { navOpen, toggleNav } = useNavigation();
  const { gateways } = useGateways();
  const { wallet } = useWalletContext();
  const { theme } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const matchRoute = useMatchRoute();
  
  // Check if user is in guest mode
  const isGuestMode = useMemo(() => WalletTypeDetector.isGuestWallet(wallet), [wallet]);

  // Get the appropriate logo based on theme
  const logoPath = theme === 'dark' 
    ? '/images/Sourceful Energy Dashboard Dark.svg'
    : '/images/Sourceful Energy Dashboard Light.svg';

  // Calculate total notifications
  const totalNotifications = useMemo(() => {
    if (!gateways) return 0;
    return gateways.reduce((total, gateway) => {
      return total + (gateway.state?.status?.messages?.length || 0);
    }, 0);
  }, [gateways]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleNav();
  };

  return (
    <header className="z-50">
      <nav className="bg-[#1D1D1D] dark:bg-[#1D1D1D] light:bg-white border-b border-[#292929] dark:border-[#292929] light:border-gray-200 px-4 py-3 lg:p-4 lg:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center text-gray-50">
              <img src={logoPath} alt="Sourceful Energy" className="h-auto w-[180px] lg:w-[180px]" />
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden lg:flex items-center gap-6">
              <Link
                to="/rewards"
                className={`flex items-center text-base transition-colors ${
                  matchRoute({ to: "/rewards" }) 
                    ? "text-black dark:text-white" 
                    : "text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white"
                }`}
              >
                <BoltIcon className="h-5 w-5 mr-1.5 text-[#00FF84]" />
                <span>Rewards</span>
              </Link>
              <Link
                to="/leaderboard"
                className={`flex items-center text-base transition-colors ${
                  matchRoute({ to: "/leaderboard" }) 
                    ? "text-black dark:text-white" 
                    : "text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white"
                }`}
              >
                <CrownIcon className="h-5 w-5 mr-1.5 text-[#00FF84]" />
                <span>Leaderboard</span>
              </Link>
              {isGuestMode ? (
                <div
                  className="flex items-center text-base text-gray-500 cursor-not-allowed"
                  title="Not available in Guest Mode"
                >
                  <BellIcon className="h-5 w-5 mr-1.5 text-gray-500" />
                  <span>Notifications</span>
                </div>
              ) : (
                <Link
                  to="/notifications"
                  className={`flex items-center text-base transition-colors ${
                    matchRoute({ to: "/notifications" }) 
                      ? "text-black dark:text-white" 
                      : "text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white"
                  }`}
                >
                  <BellIcon className="h-5 w-5 mr-1.5 text-[#00FF84]" />
                  <span>Notifications</span>
                  {totalNotifications > 0 && (
                    <span className="ml-2 bg-gray-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {totalNotifications}
                    </span>
                  )}
                </Link>
              )}
              <div className="relative" ref={dropdownRef}>
                {isGuestMode ? (
                  <div
                    className="flex items-center text-base text-gray-500 cursor-not-allowed"
                    title="Not available in Guest Mode"
                  >
                    <PlusIcon className="h-5 w-5 mr-1.5 text-gray-500" />
                    <span>Add Gateway</span>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center text-base text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white cursor-pointer transition-colors"
                    >
                      <PlusIcon className="h-5 w-5 mr-1.5 text-[#00FF84]" />
                      <span>Add Gateway</span>
                    </button>
                    
                    {isDropdownOpen && (
                      <div className="absolute right-0 top-full mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] z-50">
                        <div className="py-1" role="menu">
                          {isGuestMode ? (
                            <div
                              className="flex items-center text-base text-gray-500 cursor-not-allowed"
                              title="Not available in Guest Mode"
                            >
                              <PlusCpuIcon className="h-5 w-5 mr-1.5 text-gray-500" />
                              <span>Add Energy Gateway</span>
                            </div>
                          ) : (
                            <Link
                              to="/onboarding-wizard"
                              className={`flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#292929] hover:text-black dark:hover:text-white w-full text-left`}
                              role="menuitem"
                              onClick={() => setIsDropdownOpen(false)}
                            >
                              <PlusCpuIcon className="h-4 w-4 mr-2 text-[#00FF84]" />
                              <span>Add Energy Gateway</span>
                            </Link>
                          )}
                          {isGuestMode ? (
                            <div
                              className="flex items-center text-base text-gray-500 cursor-not-allowed"
                              title="Not available in Guest Mode"
                            >
                              <CloudIcon className="h-5 w-5 mr-1.5 text-gray-500" />
                              <span>Add Cloud Gateway</span>
                            </div>
                          ) : (
                            <Link
                              to="/onboarding/cloud"
                              className={`flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#292929] hover:text-black dark:hover:text-white w-full text-left`}
                              role="menuitem"
                              onClick={() => setIsDropdownOpen(false)}
                            >
                              <CloudIcon className="h-4 w-4 mr-2 text-[#00FF84]" />
                              <span>Add Cloud Gateway</span>
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <ThemeToggle />
            </div>
            
            <div className="hidden lg:block">
              <WalletConnect variant="header" />
            </div>
            
            <div className="lg:hidden flex items-center gap-3">
              <ThemeToggle />
              <button 
                className="relative w-[28px] h-[28px] focus:outline-none flex items-center justify-center"
                onMouseDown={handleMenuClick}
              >
                <div className={`absolute w-[24px] h-[3px] bg-[#00FF84] rounded-full transition-all duration-300 ${
                  navOpen ? 'rotate-45 top-[12.5px]' : 'rotate-0 top-[5px]'
                }`} />
                <div className={`absolute w-[24px] h-[3px] bg-[#00FF84] rounded-full transition-all duration-300 ${
                  navOpen ? 'opacity-0 top-[12.5px]' : 'opacity-100 top-[12.5px]'
                }`} />
                <div className={`absolute w-[24px] h-[3px] bg-[#00FF84] rounded-full transition-all duration-300 ${
                  navOpen ? '-rotate-45 top-[12.5px]' : 'rotate-0 top-[20px]'
                }`} />
              </button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
